import {
  CreditCardIcon,
  Home03Icon,
  UserMultiple02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { IconSvgElement } from "@hugeicons/react";
import { ApplicantType } from "shared/types";
import {
  InviteFlowEntry,
  InviteFlowSettings,
  Template
} from "types/landlord/verificationTemplate";
import { INVITE_FLOW_LEGACY_DEFAULT, INVITE_FLOW_ROLES } from "../../constant";

type RoleMeta = {
  inviterPlural: string;
  inviteeLabel: string;
  inviteeDescription: string;
  inviteeIcon: IconSvgElement;
};

export const ROLE_META: Record<ApplicantType, RoleMeta> = {
  [ApplicantType.Applicant]: {
    inviterPlural: "applicants",
    inviteeLabel: "Co-applicant (18+)",
    inviteeDescription:
      "Will live in the property and will be financially responsible for the lease.",
    inviteeIcon: UserMultiple02Icon
  },
  [ApplicantType.CoSigner]: {
    inviterPlural: "guarantors",
    inviteeLabel: "Guarantor / Co-signer",
    inviteeDescription:
      "Will not live in the property but will be financially responsible for the lease.",
    inviteeIcon: CreditCardIcon
  },
  [ApplicantType.Occupant]: {
    inviterPlural: "occupants",
    inviteeLabel: "Occupant (18+)",
    inviteeDescription:
      "Will live in the property but will not be financially responsible for the lease.",
    inviteeIcon: Home03Icon
  }
};

export const INVITEE_DISPLAY_ORDER: ApplicantType[] = [
  ApplicantType.Applicant,
  ApplicantType.Occupant,
  ApplicantType.CoSigner
];

export const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

export const resolveInviteFlow = (template: Template): InviteFlowSettings => ({
  [ApplicantType.Applicant]:
    template.invite_flow?.[ApplicantType.Applicant] ??
    INVITE_FLOW_LEGACY_DEFAULT[ApplicantType.Applicant],
  [ApplicantType.CoSigner]:
    template.invite_flow?.[ApplicantType.CoSigner] ??
    INVITE_FLOW_LEGACY_DEFAULT[ApplicantType.CoSigner],
  [ApplicantType.Occupant]:
    template.invite_flow?.[ApplicantType.Occupant] ??
    INVITE_FLOW_LEGACY_DEFAULT[ApplicantType.Occupant]
});

const withInviteFlowEntry = (
  template: Template,
  role: ApplicantType,
  entry: InviteFlowEntry
): Template => ({
  ...template,
  invite_flow: { ...resolveInviteFlow(template), [role]: entry }
});

export const withInviteFlowEnabled = (
  template: Template,
  role: ApplicantType,
  enabled: boolean
): Template => {
  const entry = resolveInviteFlow(template)[role];

  if (!enabled)
    return withInviteFlowEntry(template, role, {
      enabled: false,
      allowed_roles: entry.allowed_roles
    });

  const validOnTemplate = INVITE_FLOW_ROLES.filter((r) =>
    isRoleEnabledOnTemplate(template, r)
  );
  const kept = entry.allowed_roles.filter((r) =>
    isRoleEnabledOnTemplate(template, r)
  );

  return withInviteFlowEntry(template, role, {
    enabled: true,
    allowed_roles: kept.length ? kept : validOnTemplate
  });
};

const toggleAllowedRole = (
  allowedRoles: ApplicantType[],
  role: ApplicantType
) =>
  INVITE_FLOW_ROLES.filter((r) =>
    r === role ? !allowedRoles.includes(r) : allowedRoles.includes(r)
  );

export const isRoleEnabledOnTemplate = (
  template: Template,
  role: ApplicantType
) =>
  role === ApplicantType.Applicant ||
  Boolean(template.settings[role]?.enabled_screening_services);

export const withAllowedRoleToggled = (
  template: Template,
  inviterRole: ApplicantType,
  role: ApplicantType
): Template => {
  const entry = resolveInviteFlow(template)[inviterRole];
  const allowed_roles = toggleAllowedRole(entry.allowed_roles, role);

  const hasInvitableRole = allowed_roles.some((r) =>
    isRoleEnabledOnTemplate(template, r)
  );

  return withInviteFlowEntry(template, inviterRole, {
    enabled: hasInvitableRole ? entry.enabled : false,
    allowed_roles
  });
};

export const isRoleInvitable = (
  template: Template,
  inviterRole: ApplicantType,
  role: ApplicantType
) =>
  isRoleEnabledOnTemplate(template, role) &&
  resolveInviteFlow(template)[inviterRole].allowed_roles.includes(role);

export const withRoleAllowedForEnabledInviters = (
  template: Template,
  role: ApplicantType
): Template => {
  const inviteFlow = resolveInviteFlow(template);

  return {
    ...template,
    invite_flow: INVITE_FLOW_ROLES.reduce<InviteFlowSettings>(
      (acc, inviterRole) => {
        const entry = inviteFlow[inviterRole];

        if (!entry.enabled || entry.allowed_roles.includes(role)) return acc;

        return {
          ...acc,
          [inviterRole]: {
            enabled: true,
            allowed_roles: INVITE_FLOW_ROLES.filter(
              (r) => r === role || entry.allowed_roles.includes(r)
            )
          }
        };
      },
      inviteFlow
    )
  };
};
